import UnitType, {
    unitTypeTransformer,
} from '@r6digital/rapidstor-types/react/entities/UnitType';
import {
    RapidStorResponse,
    RapidStorUnitList,
} from '@r6digital/rapidstor-types/types/Api';
import InsuranceCoverage from '@r6digital/rapidstor-types/types/InsuranceCoverage';
import {
    BaseReservation,
    RapidStorReservationRequest,
} from '@r6digital/rapidstor-types/types/Reservation';

import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { RAPIDSTOR_BASE_URL } from './config';

const baseQuery = fetchBaseQuery({
    baseUrl: RAPIDSTOR_BASE_URL,
});

interface BaseQuery {
    corp: string;
    site: string;
}

interface UnitTypesRequestPaylod extends BaseQuery {
    locationCodes: string[];
}

interface UnitTypesResponse {
    unitTypes: UnitType[];
}

export const rapidstorApi = createApi({
    reducerPath: 'rapidstorApi',
    baseQuery: retry(baseQuery, { maxRetries: 2 }),
    keepUnusedDataFor: 3600,
    tagTypes: ['Location', 'UnitList', 'Personas'],
    endpoints: (builder) => ({
        getUnitListByType: builder.query<UnitTypesResponse, UnitTypesRequestPaylod>({
            query: ({ corp, site, locationCodes }) => ({
                url: `/api/v1/unit-list/by-type`,
                method: 'POST',
                body: {
                    sites: locationCodes,
                },
                headers: {
                    corp,
                    site,
                },
            }),
            transformResponse: (
                resp: RapidStorResponse<RapidStorUnitList>
            ): UnitTypesResponse => {
                return {
                    unitTypes: resp.results.unitTypes.map((u) => {
                        return unitTypeTransformer(u, undefined);
                    }),
                };
            },
        }),
        getInsuranceList: builder.query<
            RapidStorResponse<InsuranceCoverage[]>,
            BaseQuery
        >({
            query: ({ corp, site }) => ({
                url: '/api/v1/insurance',
                method: 'GET',
                headers: {
                    corp,
                    site,
                },
            }),
        }),
        postReservation: builder.mutation<BaseReservation, RapidStorReservationRequest>({
            query: ({
                locationCode,
                corpCode,
                payload,
            }: RapidStorReservationRequest) => ({
                url: `/api/v1/reservation`,
                method: 'POST',
                body: payload,
                headers: {
                    site: locationCode,
                    corp: corpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<BaseReservation>) => {
                return resp.results;
            },
        }),
    }),
});

export const {
    useGetUnitListByTypeQuery,
    useGetInsuranceListQuery,
    usePostReservationMutation,
} = rapidstorApi;
